import { apiClient } from "./api-client";

// Staff Registry — dedicated admin management, entirely separate from customers.
// Mirrors the backend AdminManagementController (/admin/api/v1/admins), which is
// gated hasRole('ADMIN_SUPER'). All request/response shapes follow the DTOs in
// modules/auth (AdminView / CreateAdminPayload / UpdateAdminPayload).

export type AdminLevel = "SUB" | "TENANT_SUPER" | "BOOTSTRAP_SUPER";
export type AdminUserStatus = "PENDING_PROFILE" | "ACTIVE" | "SUSPENDED" | "DELETED";
export type AdminPasswordStatus = "ACTIVE" | "PENDING_SETUP";
export type AdminCapabilityRole = "ADMIN_VIEWER" | "ADMIN_OPS" | "ADMIN_MARKETING" | "ADMIN_SUPER";

export interface AdminView {
  id: number;
  tenantId?: string;
  email?: string;
  fullName?: string;
  adminLevel?: AdminLevel;
  role?: AdminCapabilityRole | string;
  status?: AdminUserStatus | string;
  passwordStatus?: AdminPasswordStatus | string;
  /** True while a lockout is in effect (BLK-API-018 — server-computed). */
  locked?: boolean;
  lockedUntil?: string | null;
  createdBy?: number;
  createdAt?: string;
}

export interface CreateAdminInput {
  fullName: string;
  email: string;
  tenantId: string;
  adminLevel: "TENANT_SUPER" | "SUB"; // BOOTSTRAP_SUPER is not creatable via API
  role: AdminCapabilityRole;
}

export interface UpdateAdminInput {
  fullName?: string;
  role?: AdminCapabilityRole;
  locale?: string;
}

const BASE = "/admin/api/v1/admins";

export function listAdmins(page = 0, size = 20): Promise<AdminView[]> {
  return apiClient.request<AdminView[]>(`${BASE}?page=${page}&size=${size}`);
}

export function getAdmin(id: number): Promise<AdminView> {
  return apiClient.request<AdminView>(`${BASE}/${id}`);
}

export function createAdmin(input: CreateAdminInput): Promise<AdminView> {
  return apiClient.request<AdminView>(BASE, { method: "POST", body: input });
}

export function updateAdmin(id: number, input: UpdateAdminInput): Promise<AdminView> {
  return apiClient.request<AdminView>(`${BASE}/${id}`, { method: "PATCH", body: input });
}

/** Lifecycle actions — each records a security-audit event server-side. */
export function activateAdmin(id: number): Promise<AdminView> {
  return apiClient.request<AdminView>(`${BASE}/${id}/activate`, { method: "POST" });
}
export function deactivateAdmin(id: number): Promise<AdminView> {
  return apiClient.request<AdminView>(`${BASE}/${id}/deactivate`, { method: "POST" });
}
export function lockAdmin(id: number): Promise<AdminView> {
  return apiClient.request<AdminView>(`${BASE}/${id}/lock`, { method: "POST" });
}
export function unlockAdmin(id: number): Promise<AdminView> {
  return apiClient.request<AdminView>(`${BASE}/${id}/unlock`, { method: "POST" });
}
export function resetAdminPassword(id: number): Promise<AdminView> {
  return apiClient.request<AdminView>(`${BASE}/${id}/reset-password`, { method: "POST" });
}
/** Soft-delete: retained for audit, email freed, sessions revoked. 204 on success. */
export function deleteAdmin(id: number): Promise<void> {
  return apiClient.request<void>(`${BASE}/${id}`, { method: "DELETE" });
}

// --- Display metadata -------------------------------------------------------

export const CREATABLE_ROLES: { value: AdminCapabilityRole; label: string; hint: string }[] = [
  { value: "ADMIN_SUPER", label: "Super Admin", hint: "Full access incl. staff + audit" },
  { value: "ADMIN_OPS", label: "Operations", hint: "Orders, catalog, inventory, customers" },
  { value: "ADMIN_MARKETING", label: "Marketing", hint: "Catalog + analytics" },
  { value: "ADMIN_VIEWER", label: "Viewer", hint: "Read-only dashboards + analytics" },
];

export const CREATABLE_LEVELS: { value: "TENANT_SUPER" | "SUB"; label: string; hint: string }[] = [
  { value: "SUB", label: "Sub Admin", hint: "Operational admin within a tenant" },
  {
    value: "TENANT_SUPER",
    label: "Tenant Super Admin",
    hint: "Manages a tenant and its sub-admins",
  },
];

export function adminLevelLabel(level: string | undefined): string {
  switch (level) {
    case "BOOTSTRAP_SUPER":
      return "Bootstrap Super Admin";
    case "TENANT_SUPER":
      return "Tenant Super Admin";
    case "SUB":
      return "Sub Admin";
    default:
      return level ?? "—";
  }
}
